import React, { useEffect, useState } from "react";
import SkeletonList from "../common/SkeletonList";
import ChatBar from "./chatbar";
import Contactmodel from "./contactmodel";
import { getPlanDisplayName } from "../../services/razorpayService";
// helpers
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

const buildImageUrl = (path) => {
  if (!path) return "./chatscreen/profile.svg";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return `${process.env.REACT_APP_API_BASE_URL}${path}`;
  return path;
};

const formatDeviceDate = (value) => {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

function DeviceHistoryModal({ isOpen, onClose, deviceHistory, loading }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Device History</h2>
              <p className="text-sm text-gray-500">Latest login sessions</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
              aria-label="Close"
            >
              <img src="../admin/chatbar/close.svg" alt="close" className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0 space-y-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
          {loading ? (
            <SkeletonList count={4} />
          ) : deviceHistory.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No device history found</div>
          ) : (
            deviceHistory.map((device, index) => (
              <div
                key={`${device.date || index}-${index}`}
                className="rounded-2xl border border-gray-100 bg-[#F9F8FF] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {device.device_name || "Unknown device"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {device.device_type || "Device type unknown"}
                      {device.source ? ` • ${device.source}` : ""}
                    </p>
                  </div>
                  <span className="text-xs rounded-full bg-white px-3 py-1 text-gray-600 whitespace-nowrap">
                    {formatDeviceDate(device.date)}
                  </span>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <p className="truncate">IP: {device.ip_address || "-"}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ContactDetails Component
function ContactDetails({ contact, details, loading, isMobile, onBack }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("contacts");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  if (!contact) {
    return (
      <section className="p-6 bg-[#F3F0FF] w-full min-[1000px]:flex-1 min-w-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h2 className="text-2xl font-semibold text-gray-600">Select a contact to view details</h2>
          <p className="text-gray-500 mt-2">Click on any contact from the left sidebar to see their information</p>
        </div>
      </section>
    );
  }

  const profile = details?.profile || {};
  const invitedContacts = details?.total_invited_list || [];
  const acceptedInvites = details?.invited_accept_list || [];
  const deviceHistory = profile?.device_history || [];
  const storage = profile?.total_storage_user || {};

  const formatBytesToMB = (bytes) => {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return "0 MB";
    const mbValue = Number(bytes) / (1024 * 1024);
    if (mbValue >= 1024) {
      const gbValue = mbValue / 1024;
      return `${gbValue.toFixed(gbValue >= 10 ? 0 : 2)} GB`;
    }

    return `${mbValue.toFixed(mbValue >= 10 ? 0 : 2)} MB`;
  };
  return (
    <section className="p-4 sm:p-6 pb-6 bg-[#F3F0FF] w-full min-[1000px]:flex-1 min-w-0 h-full min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
      <div className="max-w-4xl mx-auto pb-4">
        {isMobile && (
          <div className="mb-4">
            <button type="button" onClick={onBack} className="inline-flex items-center px-3 py-2 rounded-lg bg-white border border-gray-200">
              ← Back
            </button>
          </div>
        )}
        {/* Contact Header */}
        <div className="pb-6">
          <div className="flex flex-col min-[1000px]:flex-row min-[1000px]:items-center min-[1000px]:justify-between gap-6">
            <div>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {loading ? (
                  <div className="h-24 w-24 rounded-full bg-gray-200 animate-pulse" />
                ) : (
                  <img
                    src={buildImageUrl(profile.profile_image_url || contact.avatar)}
                    alt={contact.name || profile.full_name}
                    className="h-24 w-24 rounded-full object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  {loading ? (
                    <>
                      <div className="h-6 w-48 bg-gray-200 rounded mb-3 animate-pulse" />
                      <div className="h-4 w-32 bg-gray-200 rounded mb-1 animate-pulse" />
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    </>
                  ) : (
                    <>
                      <h1 className="text-2xl font-bold text-gray-800">{contact.name || profile.full_name}</h1>
                      <p className="text-gray-600">{contact.phone || `${profile.country_code || ""}${profile.phone_number || ""}`}</p>
                      <p className="text-gray-600">{contact.email || profile.email}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 min-[1000px]:grid-cols-2 gap-4 mt-6">
                <div className="bg-white rounded-3xl shadow-lg p-4 text-left cursor-pointer" onClick={() => { setModalType("contacts"); setIsModalOpen(true); }}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Contact Upload</h3>
                  {loading ? (
                    <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.total_contact_upload ?? "-"}</p>
                  )}
                </div>
                <div className="bg-white rounded-3xl shadow-lg p-4 text-left">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Invited</h3>
                  {loading ? (
                    <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.total_invited ?? "-"}</p>
                  )}
                </div>
                <div className="bg-white rounded-3xl shadow-lg p-4 text-left cursor-pointer" onClick={() => { setModalType("mutual"); setIsModalOpen(true); }}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Mutual Contacts</h3>
                  {loading ? (
                    <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.mutual_contacts ?? "-"}</p>
                  )}
                </div>
                <div className="bg-white rounded-3xl shadow-lg p-4 text-left">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Invited Accept</h3>
                  {loading ? (
                    <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.invited_accept ?? "-"}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="w-full min-[1000px]:w-[435px] flex flex-col gap-4">
            <div className="rounded-3xl border border-gray-100 bg-[#F9F8FF] p-5 shadow-lg">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Storage Usage</h2>
                  {/* <p className="text-sm text-gray-500">Current plan</p> */}
                </div>
                <div className="rounded-2xl bg-white px-3 py-2 text-right shadow-sm">
                  <p className="text-sm font-semibold text-[#6A35FF]">{getPlanDisplayName(storage?.plan_type) || "-"}</p>
                </div>
              </div>

              {loading ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-3 w-full rounded-full bg-gray-200" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-14 rounded-2xl bg-gray-100" />
                    <div className="h-14 rounded-2xl bg-gray-100" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>{storage?.used_gb ?? 0} GB used</span>
                      <span>{storage?.limit_gb ?? 0} GB limit</span>
                    </div>
                    <div className="h-3 rounded-full bg-white overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#7C4DFF] to-[#6A35FF]"
                        style={{ width: `${Math.min(storage?.percent_used ?? 0, 100)}%` }}
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {storage?.percent_used ?? 0}% of your storage is used.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-2xl bg-white p-3 shadow-sm">
                      <p className="text-gray-500 mb-1">Used</p>
                      <p className="font-semibold text-gray-800">{formatBytesToMB(storage?.used_bytes)}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-3 shadow-sm">
                      <p className="text-gray-500 mb-1">Limit</p>
                      <p className="font-semibold text-gray-800">{formatBytesToMB(storage?.limit_bytes)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsHistoryModalOpen(true)}
              className="rounded-3xl border border-gray-100 bg-white p-5 shadow-lg text-left cursor-pointer hover:bg-[#F9F8FF] transition-colors w-full"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Device History</h2>
                  <p className="text-sm text-gray-500">Latest login sessions</p>
                  {/* {loading ? (
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse mt-2" />
                  ) : (
                    <p className="text-sm font-semibold text-[#6A35FF] mt-2">{deviceHistory.length} sessions</p>
                  )} */}
                </div>
                <span className="text-sm font-semibold text-[#6A35FF] whitespace-nowrap">View History →</span>
              </div>
            </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {/* <div className="grid grid-cols-1 min-[1000px]:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-3xl shadow-lg p-4 text-left cursor-pointer" onClick={() => { setModalType("contacts"); setIsModalOpen(true); }}>
                        <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Contact Upload</h3>
                        {loading ? (
                          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.total_contact_upload ?? "-"}</p>
                        )}
                    </div>
                  <div className="bg-white rounded-3xl shadow-lg p-4 text-left">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Invited</h3>
                        {loading ? (
                          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.total_invited ?? "-"}</p>
                        )}
                    </div>
                  <div className="bg-white rounded-3xl shadow-lg p-4 text-left cursor-pointer" onClick={() => { setModalType("mutual"); setIsModalOpen(true); }}>
                        <h3 className="text-sm font-semibold text-gray-600 mb-1">Mutual Contacts</h3>
                        {loading ? (
                          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.mutual_contacts ?? "-"}</p>
                        )}
                    </div>
                  <div className="bg-white rounded-3xl shadow-lg p-4 text-left">
                        <h3 className="text-sm font-semibold text-gray-600 mb-1">Invited Accept</h3>
                        {loading ? (
                          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          <p className="text-2xl font-bold text-gray-800 aoboshi-one">{details?.invited_accept ?? "-"}</p>
                        )}
                    </div>
                </div> */}

        {/* Invited Contacts Section */}
        <div className="flex flex-col min-[1000px]:flex-row justify-between gap-6">
          <div className="bg-white rounded-3xl shadow-lg p-4 min-[1000px]:p-6  min-[1000px]:pt-2 w-full min-[1000px]:w-1/2 flex flex-col h-[55vh] min-[1000px]:h-[calc(100dvh-450px)]">
            <h2 className="text-lg font-semibold text-gray-800 mb-1 flex-shrink-0">Total Invited</h2>
            <div className="space-y-3 p-2 flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
              {loading ? (
                <SkeletonList count={6} />
              ) : invitedContacts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No invited contacts</div>
              ) : (
                invitedContacts.map((invited, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 py-1 rounded-[100px] border border-[#4F46E5] shadow-[1px_4px_4px_0_rgba(117,117,117,0.55)] cursor-pointer transition-all duration-200 bg-[#797C7B] bg-opacity-[11%]"
                  >
                    <img
                      src={buildImageUrl(invited.profile_image_url)}
                      alt={invited.full_name || invited.first_name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1 heading-font">
                      {invited.full_name || `${invited.first_name || ""} ${invited.last_name || ""}`}
                      <p className="body-font truncate">
                        {(invited.country_code || "") + (invited.phone_number || "")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Accepted Invites Section */}
          <div className="bg-white rounded-3xl shadow-lg p-4 min-[1000px]:p-6  min-[1000px]:pt-2 w-full min-[1000px]:w-1/2 flex flex-col h-[55vh] min-[1000px]:h-[calc(100dvh-450px)]">
            <h2 className="text-lg font-semibold text-gray-800 mb-1 flex-shrink-0">Invited Accept</h2>
            <div className="space-y-3 px-2 py-2 flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
              {loading ? (
                <SkeletonList count={6} />
              ) : acceptedInvites.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No accepted invites</div>
              ) : (
                acceptedInvites.map((accepted, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 py-1 rounded-[100px] border border-[#4F46E5] shadow-[1px_4px_4px_0_rgba(117,117,117,0.55)] cursor-pointer transition-all duration-200 bg-[#797C7B] bg-opacity-[11%]"
                  >
                    <img
                      src={buildImageUrl(accepted.profile_image_url)}
                      alt={accepted.full_name || accepted.first_name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1 heading-font">
                      {accepted.full_name || `${accepted.first_name || ""} ${accepted.last_name || ""}`}
                      <p className="body-font truncate">
                        {(accepted.country_code || "") + (accepted.phone_number || "")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <Contactmodel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contact={contact}
        type={modalType}
      />
      <DeviceHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        deviceHistory={deviceHistory}
        loading={loading}
      />
    </section>
  );
}

// Main AdminContact Component
function AdminContact() {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 900 : false);
  const [mobileView, setMobileView] = useState("list");

  const token = getCookie("admin_token");

  const fetchContacts = async () => {
    if (!token) {
      setError("Not authenticated");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/connect/users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to load contacts");
      const json = await res.json();
      const list = (json?.data || []).map((u) => ({
        id: u.user_id,
        name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim(),
        email: u.email,
        phone: (u.country_code || "") + (u.phone_number || ""),
        avatar: buildImageUrl(u.profile_image_url),
        _raw: u,
      }));
      setContacts(list);
      if (list.length) {
        setSelectedContact(list[0]);
      }
    } catch (e) {
      setError(e.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async (userId) => {
    if (!token || !userId) return;
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("user_id", userId);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/connect/user/details`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: body.toString(),
      });
      if (!res.ok) throw new Error("Failed to load details");
      const json = await res.json();
      const details = json?.data?.[0] || null;
      setSelectedDetails(details);
    } catch (e) {
      setSelectedDetails(null);
      setError(e.message || "Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mm = window.matchMedia("(max-width: 899px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);
    setIsMobile(mm.matches);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (selectedContact?.id) {
      fetchDetails(selectedContact.id);
    } else {
      setSelectedDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id]);

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    if (isMobile) setMobileView("detail");
  };

  return (
    <div className="h-[calc(100dvh-1rem)] min-h-0 overflow-hidden">
      <div className="rounded-2xl shadow-lg overflow-hidden h-full">
        <div className="flex flex-col min-[1000px]:flex-row h-full min-h-0">
          <div className={`${isMobile && mobileView === 'detail' ? 'hidden' : ''} min-[1000px]:block w-full min-[1000px]:w-[360px] min-[1000px]:flex-shrink-0 h-full min-h-0`}>
            <ChatBar
              selectedContact={selectedContact}
              onContactSelect={handleContactSelect}
              contacts={contacts}
              loading={loading}
            />
          </div>
          <div className={`${isMobile && mobileView === 'list' ? 'hidden' : ''} w-full min-[1000px]:block min-[1000px]:flex-1 min-w-0 h-full min-h-0 overflow-hidden`}>
            <ContactDetails contact={selectedContact} details={selectedDetails} loading={loading} isMobile={isMobile} onBack={() => setMobileView('list')} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminContact;